import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as InstitutionProcedureActions from "../../../actions/institutionProcedure";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    InstitutionProcedureBreadcrumbs,
    InstitutionProcedureCreateBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { InstitutionProcedureSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { IInstitutionProcedureReduxFormState } from "../../../reducers/reactReduxForms/institutionProcedure";

import "./InstitutionProcedurePage.scss";

interface InstitutionProcedurePageProps {
    form: Dtos.InstitutionProcedure & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IInstitutionProcedureReduxFormState;
    institutionProcedure: Dtos.InstitutionProcedure;
    lookups: Dtos.Lookup[];
    loadingInstitutionProcedure: boolean;
    loadInstitutionProcedureSuccess: boolean;
    loadInstitutionProcedureFailure: boolean;
    loadInstitutionProcedureErrorCode: string;
    savingInstitutionProcedure: boolean;
    saveInstitutionProcedureSuccess: boolean;
    saveInstitutionProcedureFailure: boolean;
    creatingInstitutionProcedure: boolean;
    createInstitutionProcedureSuccess: boolean;
    createInstitutionProcedureFailure: boolean;
    validatingInstitutionProcedure: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureClearActionCreator;
    loadInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureLoadByIdActionCreator;
    saveInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureSaveActionCreator;
    createInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureCreateActionCreator;
    validateInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionProcedure";

const InstitutionProcedurePage: FC<InstitutionProcedurePageProps> = ({
    form,
    formState,
    formProperties,
    institutionProcedure,
    lookups,
    loadingInstitutionProcedure,
    loadInstitutionProcedureFailure,
    loadInstitutionProcedureErrorCode,
    savingInstitutionProcedure,
    saveInstitutionProcedureSuccess,
    saveInstitutionProcedureFailure,
    creatingInstitutionProcedure,
    validatingInstitutionProcedure,
    validationFailures,
    permissions,
    clearInstitutionProcedure,
    loadInstitutionProcedure,
    saveInstitutionProcedure,
    createInstitutionProcedure,
    validateInstitutionProcedure,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const { institutionProcedureId } = useParams<{institutionProcedureId: string;}>();
    const institutionIdNumber = parseInt(institutionId, 10);
    const institutionProcedureIdNumber = parseInt(institutionProcedureId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Procedure Institution
    useEffect(() => {
        if (
            institutionIdNumber &&
            institutionProcedureIdNumber &&
            !institutionProcedure
        ) {
            setDrawerItems(
                InstitutionProcedureSubMenu(
                    institutionIdNumber,
                    institutionProcedureIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                InstitutionProcedureBreadcrumbs(
                    institutionIdNumber,
                    institutionProcedureIdNumber
                )
            );

            loadInstitutionProcedure(institutionProcedureIdNumber);
        } else if (
            institutionIdNumber &&
            !institutionProcedureIdNumber &&
            !institutionProcedure
        ) {
            setBreadcrumbs(
                InstitutionProcedureCreateBreadcrumbs(institutionIdNumber)
            );
            setDrawerItems([]);
            createInstitutionProcedure(institutionIdNumber);
        } else if (institutionProcedure && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionIdNumber,
        institutionProcedureIdNumber,
        institutionProcedure,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitutionProcedure,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionProcedureFailure) {
            navigate(`/error/${loadInstitutionProcedureErrorCode}`);
        }
    }, [
        loadInstitutionProcedureFailure,
        loadInstitutionProcedureErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            institutionIdNumber &&
            institutionProcedureIdNumber &&
            institutionProcedure?.institution &&
            institutionProcedure?.name
        ) {
            setBreadcrumbs(
                InstitutionProcedureBreadcrumbs(
                    institutionIdNumber,
                    institutionProcedureIdNumber,
                    institutionProcedure.institution?.name,
                    institutionProcedure?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institutionProcedure,
        institutionProcedureIdNumber,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionProcedureBreadcrumbs,
    ]);

    // Reload institution after saving
    useEffect(() => {
        if (!savingInstitutionProcedure && saveInstitutionProcedureSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution procedure saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/institutions/${institutionIdNumber}/procedures`);
            } else if (!institutionProcedureIdNumber) {
                navigate(`/institutions/${institutionIdNumber}/procedures/${institutionProcedure?.id}/details`);
                clearInstitutionProcedure();
            } else {
                loadInstitutionProcedure(institutionProcedureIdNumber);
            }
        }
    }, [
        savingInstitutionProcedure,
        saveInstitutionProcedureSuccess,
        loadInstitutionProcedure,
        institutionProcedureIdNumber,
        navigate,
        saveAndReturn,
        clearInstitutionProcedure,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionProcedure && saveInstitutionProcedureFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingInstitutionProcedure,
        saveInstitutionProcedureSuccess,
        loadInstitutionProcedure,
        institutionProcedureIdNumber,
    ]);

    // Cleanup effect to clear the institution on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionProcedure();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionProcedure, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionProcedure = {} as Dtos.InstitutionProcedure;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveInstitutionProcedure(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionProcedure) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/institutions/${institutionIdNumber}/procedures`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionProcedure = {} as Dtos.InstitutionProcedure;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionProcedure }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionProcedure = {} as Dtos.InstitutionProcedure;

            if (!form.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingInstitutionProcedure) {
                await validateInstitutionProcedure(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                institutionProcedureIdNumber,
                permissions,
                Dtos.Permission.InstitutionProcedureUpdate,
                Dtos.Permission.InstitutionProcedureCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionProcedure ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "institutionProcedure",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-3 col-xl-2",
                                                fieldClassName: "col-9 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "institutionProcedure",
                                        {
                                            location: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.location == null || !isUrl(form?.location)}
                                                            style={{
                                                                cursor: form?.location == null || !isUrl(form?.location)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.location);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-3 col-xl-2",
                                                fieldClassName: "col-9 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionProcedure",
                                        {
                                            dateOfProcedure: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-1 pb-xl-0 pt-0",
                                                labelClassName: "col-form-label col-3 col-xl-4",
                                                fieldClassName: "col-9 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionProcedure",
                                        {
                                            expiryDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-1 pt-xl-0",
                                                labelClassName: "col-form-label col-sm-3",
                                                fieldClassName: "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "institutionProcedure",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        institutionProcedureIdNumber,
        permissions,
        savingInstitutionProcedure,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        openLink,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionProcedureView}
            loading={
                loadingInstitutionProcedure ||
                creatingInstitutionProcedure ||
                savingInstitutionProcedure
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionProcedurePageProps> => {
    let institutionProcedure: Dtos.InstitutionProcedure | undefined = !(
        state.institutionProcedure.formData instanceof Array
    )
        ? state.institutionProcedure.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionProcedure: institutionProcedure!,
        loadingInstitutionProcedure: state.institutionProcedure.loadState && state.institutionProcedure.loadState.status == RequestState.Pending,
        loadInstitutionProcedureSuccess: state.institutionProcedure.loadState && state.institutionProcedure.loadState.status == RequestState.Success,
        loadInstitutionProcedureFailure: state.institutionProcedure.loadState && state.institutionProcedure.loadState.status == RequestState.Failure,
        loadInstitutionProcedureErrorCode: state.institutionProcedure.loadState.errorCode,
        savingInstitutionProcedure: state.institutionProcedure.saveState && state.institutionProcedure.saveState.status == RequestState.Pending,
        saveInstitutionProcedureSuccess: state.institutionProcedure.saveState && state.institutionProcedure.saveState.status == RequestState.Success,
        saveInstitutionProcedureFailure: state.institutionProcedure.saveState && state.institutionProcedure.saveState.status == RequestState.Failure,
        creatingInstitutionProcedure: state.institutionProcedure.createState && state.institutionProcedure.createState.status == RequestState.Pending,
        createInstitutionProcedureSuccess: state.institutionProcedure.createState && state.institutionProcedure.createState.status == RequestState.Success,
        createInstitutionProcedureFailure: state.institutionProcedure.createState && state.institutionProcedure.createState.status == RequestState.Failure,
        validatingInstitutionProcedure: state.institutionProcedure.validateState && state.institutionProcedure.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.institutionProcedure.lookups,
        form: state.reduxForms.institutionProcedure,
        formState: state.institutionProcedure.formState,
        formProperties: state.institutionProcedure.formProperties,
        reduxFormState: state.reduxForms.formState.institutionProcedure,
        validationFailures: institutionProcedure
            ? state.institutionProcedure.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionProcedurePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionProcedure: bindActionCreators(InstitutionProcedureActions.LoadInstitutionProcedureById, dispatch),
        clearInstitutionProcedure: bindActionCreators(InstitutionProcedureActions.Clear, dispatch),
        saveInstitutionProcedure: bindActionCreators(InstitutionProcedureActions.SaveInstitutionProcedure, dispatch),
        createInstitutionProcedure: bindActionCreators(InstitutionProcedureActions.CreateInstitutionProcedure, dispatch),
        validateInstitutionProcedure: bindActionCreators(InstitutionProcedureActions.ValidateInstitutionProcedure, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionProcedurePage);
